import WebViewer, { WebViewerInstance } from '@pdftron/webviewer';
import { APRYZE_STATIC_URL } from '@smx-ui-common/consts';
import { useAccess } from '@smx-ui-permission/hook/use-access';
import { SmxPermissionContext } from '@smx-ui-permission/provider/permission-provider';
import { PermissionCombinationType } from '@smx-ui-permission/types';
import { Spin } from 'antd';
import * as React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';

import { API } from '../../../../service/drawings/src/ui/common';
import { DrawingSheetOrDocumentIdDataType } from '../../../../service/drawings/src/ui/types/types';
import { useStateViewerAppryze } from './hook/use-state-viewer-appryze';
import { useAnnotationViewerAppryze } from './hook/use-annotation-viewer-appryze';

export type ViewerAppryzeContextType = 'drawing' | 'document' | 'attachmentScheduleItem' | 'attachmentTask';

export type ViewerAppryzeProps = {
    file: string | File | null;
    fileName?: string;
    height?: string;
    toolBar?: (instance: WebViewerInstance) => void;
    sheetData?: DrawingSheetOrDocumentIdDataType;
    displaySpinner?: boolean;
    context: ViewerAppryzeContextType;
    allowedAnnotation?: boolean;
};

export const getPermissionsList = (context: ViewerAppryzeContextType): PermissionCombinationType[] => {
    switch (context) {
        case 'drawing':
            return ['drawing.manage'];
        case 'document':
            return ['documents.manage'];
        case 'attachmentScheduleItem':
            return ['work-item.manage'];
        case 'attachmentTask':
            return ['schedule-task.manage'];
        default:
            return [];
    }
}

const TYPE_IMAGE = 'image';

export const ViewerAppryze: React.FC<ViewerAppryzeProps> = ({
    file,
    fileName,
    toolBar,
    sheetData,
    height = '186px',
    displaySpinner = false,
    context,
    allowedAnnotation=true
}) => {
    const webViewerRef = useRef<HTMLDivElement>(null);
    const [isViewerInitialized, setIsViewerInitialized] = useState(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [webViewer, setWebViewer] = useState<WebViewerInstance | null>(null);
    const intervalValueRef = useRef<number | NodeJS.Timeout>(0);

    let setupAnnotationInterception: ReturnType<typeof useAnnotationViewerAppryze>['setupAnnotationInterception'];
    let setCurrentDrawingSheetOrDocumentId: ReturnType<typeof useAnnotationViewerAppryze>['setCurrentDrawingSheetOrDocumentId'];

    if(allowedAnnotation){
         const annotationViewerAppryze = useAnnotationViewerAppryze(webViewer, sheetData);
        setupAnnotationInterception = annotationViewerAppryze.setupAnnotationInterception;
        setCurrentDrawingSheetOrDocumentId = annotationViewerAppryze.setCurrentDrawingSheetOrDocumentId;
    }

    const { displayName } = useContext(SmxPermissionContext);

    const { setupStateListeners } = useStateViewerAppryze(webViewer);

    const fullDomain = window.location.origin;

    const permission = getPermissionsList(context);

    const accessManager = useAccess(permission);

    useEffect(() => {
        let instance: WebViewerInstance | null = null;
        let cleanupStateListeners: (() => void) | null = null;
        let cleanupAnnotationInterception: (() => void) | null = null;

        const initWebViewer = async () => {
            if (!webViewerRef.current || isViewerInitialized) {
                return;
            }

            // setLoading(true);
            try {
                instance = await WebViewer.WebComponent(
                    {
                        licenseKey: API.APRYSE_LICENSE,
                        path: APRYZE_STATIC_URL,

                        isAdminUser: accessManager,
                        annotationUser: displayName,
                        enableAnnotations: true,
                        // fullAPI: true,
                        enableMeasurement: true,
                        css: accessManager ? `${fullDomain}/style-manager.css` : `${fullDomain}/style.css`,
                        //
                        // isReadOnly: true,
                        // enableRedaction: true,
                        // enableOptimizedWorkers: true,
                    },
                    webViewerRef.current,
                );

                instance.Core.documentViewer.enableReadOnlyMode();
                instance.Core.annotationManager.enableRedaction();

                toolBar?.(instance);

                if (!toolBar) {
                    // Disable unnecessary UI elements
                    instance.UI.disableElements(['default-top-header']);
                    instance.UI.disableElements(['tools-header']);
                }
                instance.UI.disableElements(['page-nav-floating-header']);

                const { documentViewer } = instance.Core;
                documentViewer.addEventListener('documentLoaded', () => {
                    const document = documentViewer.getDocument();
                    if (document.getType() === TYPE_IMAGE && instance) {
                        instance.UI.disableElements(['downloadButton']);
                    }
                });

                cleanupStateListeners = setupStateListeners(instance);
                cleanupAnnotationInterception = allowedAnnotation ? setupAnnotationInterception(instance) || null : null;

                setWebViewer(instance);
                setIsViewerInitialized(true);
            } catch (error) {
                console.error('Error initializing WebViewer:', error);
            } finally {
                setTimeout(() => {
                    // setLoading(false);
                }, 500);
            }
        };

        if (!isViewerInitialized) {
            const timeoutId = setTimeout(() => {
                initWebViewer();
            }, 100);

            return () => {
                clearTimeout(timeoutId);
            };
        }

        return () => {
            cleanupStateListeners?.();
            cleanupAnnotationInterception?.();

            if (instance) {
                instance.UI.closeDocument();
                setWebViewer(null);
                setIsViewerInitialized(false);
            }
        };
    }, [isViewerInitialized, toolBar]);

    useEffect(() => {
        if (!webViewer || !file) return;

        const loadDocument = () => {
            setLoading(true);
            try {
                webViewer.UI.loadDocument(file, {
                    filename: (file as File)?.name || fileName,
                });

                intervalValueRef.current = setInterval(() => {
                    if (webViewer.Core.documentViewer.getDocument()) {
                        if(allowedAnnotation){
                            setCurrentDrawingSheetOrDocumentId(sheetData?.sheetId || sheetData?.documentId);
                        }

                        clearInterval(intervalValueRef.current);
                        setLoading(false);
                    }
                }, 200);
            } catch (error) {
                console.error('Error loading document:', error);
            }
        };

        loadDocument();
    }, [webViewer, file]);

    return (
        <>
            <div
                ref={webViewerRef}
                style={{
                    width: '100%',
                    height,
                }}
            />
            {displaySpinner && <Spin spinning={loading} fullscreen={true}></Spin>}
        </>
    );
};
